import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Clock, Award, CheckCircle, PlayCircle, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProgressDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const t = (tr, en) => lang === 'tr' ? tr : en;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/students/me/progress/${courseId}`, {
          headers: authHeaders(),
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || t('Veri yüklenemedi', 'Could not load data'));
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error || t('Veri bulunamadı', 'Data not found')}
        </div>
      </div>
    );
  }

  const { course, enrollment, lessons, estimated_days_remaining, last_lesson, next_lesson } = data;
  const formatMinutes = (m) => `${Math.floor(m / 60)}s ${m % 60}dk`;
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang) : '-';

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="progress-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 text-sm flex items-center gap-1 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={16} /> {t('Geri', 'Back')}
      </button>

      <div className="bg-white rounded-2xl p-6 mb-4 border-2 border-gray-200">
        <div className="flex gap-4 items-start">
          {course.thumbnail && (
            <img
              src={course.thumbnail}
              alt={course.title_tr || course.title}
              className="w-28 h-20 object-cover rounded-lg"
            />
          )}
          <div className="flex-1">
            <h1 className="text-xl font-bold mb-2">{course.title_tr || course.title}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {t('Başlangıç', 'Started')}: {formatDate(enrollment.started_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {t('Son giris', 'Last access')}: {formatDate(enrollment.last_accessed)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-sm font-bold mb-1">
            <span>{t('Ilerleme', 'Progress')}</span>
            <span>{enrollment.progress}%</span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${enrollment.progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-blue-500 to-green-500"
              data-testid="progress-bar-fill"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid="stat-completed-lessons">
          <div className="text-xs text-gray-500 mb-1">{t('Tamamlanan Ders', 'Completed Lessons')}</div>
          <div className="text-2xl font-bold">
            {lessons.completed} / {lessons.total}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid="stat-time-spent">
          <div className="text-xs text-gray-500 mb-1">{t('Harcanan Süre', 'Time Spent')}</div>
          <div className="text-2xl font-bold">{formatMinutes(enrollment.time_spent_minutes)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid="stat-remaining">
          <div className="text-xs text-gray-500 mb-1">{t('Kalan Süre', 'Remaining')}</div>
          <div className="text-2xl font-bold">{formatMinutes(lessons.remaining_minutes)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid="stat-xp">
          <div className="text-xs text-gray-500 mb-1">XP</div>
          <div className="text-2xl font-bold text-yellow-600 flex items-center gap-1">
            <Award size={20} />
            {enrollment.xp_earned}
          </div>
        </div>
      </div>

      {estimated_days_remaining !== null && estimated_days_remaining > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4" data-testid="estimated-completion">
          <div className="text-sm text-blue-700">
            {t(
              `Bu tempoda yaklasik ${estimated_days_remaining} gunde tamamlarsiniz.`,
              `At this pace, you'll finish in about ${estimated_days_remaining} days.`
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {last_lesson && (
          <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid="last-lesson-card">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <CheckCircle size={12} /> {t('Son Ders', 'Last Lesson')}
            </div>
            <div className="font-bold text-sm mb-2">
              {last_lesson.title_tr || last_lesson.title}
            </div>
            <div className="text-xs text-gray-500">
              {last_lesson.duration_minutes} dk
            </div>
          </div>
        )}
        {next_lesson && (
          <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200" data-testid="next-lesson-card">
            <div className="text-xs text-green-700 mb-1 flex items-center gap-1">
              <PlayCircle size={12} /> {t('Sonraki Ders', 'Next Lesson')}
            </div>
            <div className="font-bold text-sm mb-2">
              {next_lesson.title_tr || next_lesson.title}
            </div>
            <button
              onClick={() => navigate(`/lesson/${course.course_id}/${next_lesson.lesson_id}`)}
              className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600"
              data-testid="continue-next-lesson"
            >
              {t('Devam Et', 'Continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
